import type { Map, View } from 'ol';
import { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature.js';
import type { Geometry } from 'ol/geom';
import { LineString, Point } from 'ol/geom';
import type { ModifyEvent } from 'ol/interaction/Modify';
import Modify from 'ol/interaction/Modify';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import type { Style } from 'ol/style';

import { DrawToolType } from '@nora-monorepo/nora-api-client';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { getHandleModifyPixelTolerance } from '#nora-ui/map/drawtools/draw-tool-utils';
import { getExtensionHandleStyle, getLineHandleStyle, getMidpointHandleStyle } from '#nora-ui/map/drawtools/styles/straight-line-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

type VertexHandleType = 'vertex';
type MidpointHandleType = 'midpoint';
type ExtensionHandleType = 'start-extension' | 'end-extension';
type HandleType = VertexHandleType | MidpointHandleType | ExtensionHandleType;

export class StraightLineTool extends CreateFeatureBaseDrawTool {
  override drawToolType: DrawToolType = DrawToolType.line;
  override options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private readonly modifyInteraction: Modify;
  private readonly handleSource: VectorSource;
  private readonly handleLayer: VectorLayer<VectorSource>;

  private lineFeature!: Feature<LineString>;
  private isInitialized = false;

  private currentHandleType: HandleType | null = null;

  private handleChangeListener: (() => void) | null = null;
  private modifiedHandleFeature: Feature<Geometry> | null = null;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Line';

    this.handleSource = new VectorSource();
    this.handleLayer = new VectorLayer<VectorSource>({
      source: this.handleSource,
      style: this.determineHandleStyle.bind(this),
      zIndex: 10000,
    });

    this.modifyInteraction = new Modify({
      source: this.handleSource,
      insertVertexCondition: () => false,
      pixelTolerance: getHandleModifyPixelTolerance(),
    });

    this.modifyInteraction.on('modifystart', this.onModifyStart.bind(this));
    this.modifyInteraction.on('modifyend', this.onModifyEnd.bind(this));
  }

  private determineHandleStyle(feature: FeatureLike): Style {
    const handleType = feature.get('handleType') as HandleType | undefined;
    if (handleType === 'midpoint') {
      return getMidpointHandleStyle();
    }
    if (handleType === 'start-extension' || handleType === 'end-extension') {
      return getExtensionHandleStyle();
    }
    return getLineHandleStyle();
  }

  private onModifyStart(event: ModifyEvent): void {
    this.handleLayer.setVisible(false);

    const features = event.features.getArray();
    if (features.length === 0) {
      return;
    }

    const handleFeature = features.at(0);
    if (!handleFeature) {
      return;
    }
    const handleType = handleFeature.get('handleType') as
      | HandleType
      | undefined;
    if (!handleType) {
      return;
    }

    if (handleType === 'midpoint') {
      const segmentIndex = handleFeature.get('segmentIndex') as number | undefined;
      if (segmentIndex === undefined) {
        return;
      }
      this.insertVertexFromMidpointHandle(handleFeature, segmentIndex);
      const newHandleType = handleFeature.get('handleType') as
        | HandleType
        | undefined;
      if (newHandleType === 'vertex') {
        this.currentHandleType = newHandleType;
        this.modifiedHandleFeature = handleFeature;

        const geometry = handleFeature.getGeometry() as Point;
        const listener = (): void => {
          this.updateLineFromHandleModification();
        };
        geometry.on('change', listener);
        this.handleChangeListener = listener;
      }
    }
    else if (handleType === 'vertex') {
      this.currentHandleType = handleType;
      this.modifiedHandleFeature = handleFeature;

      const geometry = handleFeature.getGeometry() as Point;
      const listener = (): void => {
        this.updateLineFromHandleModification();
      };
      geometry.on('change', listener);
      this.handleChangeListener = listener;
    }
    else if (
      handleType === 'start-extension'
      || handleType === 'end-extension'
    ) {
      this.currentHandleType = handleType;
      this.modifiedHandleFeature = handleFeature;

      const geometry = handleFeature.getGeometry() as Point;
      const listener = (): void => {
        this.handleExtensionDrag();
      };
      geometry.on('change', listener);
      this.handleChangeListener = listener;
    }
  }

  private onModifyEnd(): void {
    this.handleLayer.setVisible(true);
    if (this.handleChangeListener && this.modifiedHandleFeature) {
      const geometry = this.modifiedHandleFeature.getGeometry() as Point;
      geometry.un('change', this.handleChangeListener);
    }
    this.handleChangeListener = null;
    this.modifiedHandleFeature = null;
    this.addLineHandles();
    this.currentHandleType = null;
    this.markDirty();
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMap();
    if (!map) {
      return;
    }

    map.addLayer(this.handleLayer);
    map.addInteraction(this.modifyInteraction);

    if (!this.isInitialized) {
      this.createInitialLine();
      this.isInitialized = true;
    }
    this.addLineHandles();
    this.handleLayer.setVisible(true);
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMap();
    if (!map) {
      return;
    }

    map.removeInteraction(this.modifyInteraction);
    this.handleSource.clear();
    this.handleLayer.setVisible(false);
    map.removeLayer(this.handleLayer);
  }

  private createInitialLine(): void {
    const map: Map | undefined = this.drawToolManager.map.getOlMap();
    if (!map) {
      return;
    }

    const view: View = map.getView();
    const centerCoordinates = view.getCenter() as [number, number] | undefined;
    if (!centerCoordinates) {
      return;
    }

    const mapSize = map.getSize();
    if (!mapSize) {
      return;
    }

    const [width, height] = mapSize;
    if (width === undefined || height === undefined) {
      return;
    }
    const resolution = view.getResolution() ?? 1;
    const halfSize = (Math.min(width, height) / 4) * resolution;

    const startCoordinate: [number, number] = [
      centerCoordinates[0] - halfSize,
      centerCoordinates[1],
    ];
    const endCoordinate: [number, number] = [
      centerCoordinates[0] + halfSize,
      centerCoordinates[1],
    ];
    const lineGeometry = new LineString([startCoordinate, endCoordinate]);

    this.lineFeature = new Feature<LineString>(lineGeometry);
    this.lineFeature.setProperties(this.getDefaultProperties());
    this.lineFeature.setStyle(getFeatureStyle);

    this.editingVectorSource.clear();
    this.editingVectorSource.addFeature(this.lineFeature);
  }

  private addLineHandles(): void {
    this.handleSource.clear();

    const lineGeometry = this.lineFeature.getGeometry() as LineString;
    const coordinates = lineGeometry.getCoordinates();

    // vertex handles
    coordinates.forEach((coordinate, index) => {
      const vertexFeature = new Feature<Point>(new Point(coordinate));
      vertexFeature.set('handleType', 'vertex');
      vertexFeature.set('vertexIndex', index);
      this.handleSource.addFeature(vertexFeature);
    });

    // midpoint handles
    for (
      let segmentIndex = 0;
      segmentIndex < coordinates.length - 1;
      segmentIndex++
    ) {
      const startCoordinate = coordinates[segmentIndex];
      const endCoordinate = coordinates[segmentIndex + 1];
      const [startX, startY] = startCoordinate;
      const [endX, endY] = endCoordinate;
      if (
        startX === undefined
        || startY === undefined
        || endX === undefined
        || endY === undefined
      ) {
        continue;
      }
      const midpointCoordinate: [number, number] = [
        (startX + endX) / 2,
        (startY + endY) / 2,
      ];

      const midpointFeature = new Feature<Point>(new Point(midpointCoordinate));
      midpointFeature.set('handleType', 'midpoint');
      midpointFeature.set('segmentIndex', segmentIndex);
      this.handleSource.addFeature(midpointFeature);
    }

    // line extension handles
    const startExtension = this.computeExtensionHandle(true, 40);
    if (startExtension) {
      const startExtensionFeature = new Feature<Point>(
        new Point(startExtension),
      );
      startExtensionFeature.set('handleType', 'start-extension');
      this.handleSource.addFeature(startExtensionFeature);
    }

    const endExtension = this.computeExtensionHandle(false, 40);
    if (endExtension) {
      const endExtensionFeature = new Feature<Point>(new Point(endExtension));
      endExtensionFeature.set('handleType', 'end-extension');
      this.handleSource.addFeature(endExtensionFeature);
    }
  }

  /**
   * Computes the position for an extension handle.
   * @param isStart Indicates if the extension is at the start (true) or end (false).
   * @param offsetPx The offset in pixels to place the handle from the line.
   * @returns The coordinates for the extension handle.
   */
  private computeExtensionHandle(
    isStart: boolean,
    offsetPx: number,
  ): [number, number] | null {
    const lineGeometry = this.lineFeature.getGeometry() as LineString;
    const coordinates = lineGeometry.getCoordinates();
    if (coordinates.length < 2) {
      return null;
    }

    const view = this.drawToolManager.map.getOlMapOrThrow().getView();
    const resolution = view.getResolution() ?? 1;
    const offsetMapUnits = offsetPx * resolution;

    if (isStart) {
      // backwards
      const start = coordinates[0];
      const second = coordinates[1];
      const [startX, startY] = start;
      const [secondX, secondY] = second;
      if (
        startX === undefined
        || startY === undefined
        || secondX === undefined
        || secondY === undefined
      ) {
        return null;
      }
      const dx = secondX - startX;
      const dy = secondY - startY;
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length === 0) {
        return null;
      }
      const ux = dx / length;
      const uy = dy / length;
      const extension: [number, number] = [
        startX - ux * offsetMapUnits,
        startY - uy * offsetMapUnits,
      ];
      return extension;
    }
    else {
      // forward
      const last = coordinates[coordinates.length - 1];
      const secondLast = coordinates[coordinates.length - 2];
      const [lastX, lastY] = last;
      const [secondLastX, secondLastY] = secondLast;
      if (
        lastX === undefined
        || lastY === undefined
        || secondLastX === undefined
        || secondLastY === undefined
      ) {
        return null;
      }
      const dx = lastX - secondLastX;
      const dy = lastY - secondLastY;
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length === 0) {
        return null;
      }
      const ux = dx / length;
      const uy = dy / length;
      const extension: [number, number] = [
        lastX + ux * offsetMapUnits,
        lastY + uy * offsetMapUnits,
      ];
      return extension;
    }
  }

  /**
   * Inserts a new vertex into the LineString from a midpoint handle.
   * @param midpointHandle The feature representing the midpoint handle.
   * @param segmentIndex The index of the segment where the vertex is to be inserted.
   */
  private insertVertexFromMidpointHandle(
    midpointHandle: Feature<Geometry>,
    segmentIndex: number,
  ): void {
    const lineGeometry = this.lineFeature.getGeometry() as LineString;
    const coordinates = lineGeometry.getCoordinates();

    const midpointCoordinate = (
      midpointHandle.getGeometry() as Point
    ).getCoordinates() as [number, number];

    coordinates.splice(segmentIndex + 1, 0, midpointCoordinate);
    lineGeometry.setCoordinates(coordinates);
    this.lineFeature.changed();

    midpointHandle.set('handleType', 'vertex');
    midpointHandle.unset('segmentIndex');

    const newVertexIndex = segmentIndex + 1;
    this.handleSource.getFeatures().forEach((feature) => {
      if (feature === midpointHandle) {
        feature.set('vertexIndex', newVertexIndex);
        return;
      }
      if (feature.get('handleType') === 'vertex') {
        const existingIndex = feature.get('vertexIndex') as number;
        if (existingIndex >= newVertexIndex) {
          feature.set('vertexIndex', existingIndex + 1);
        }
      }
    });

    this.currentHandleType = 'vertex';

    const geometry = midpointHandle.getGeometry() as Point;
    const listener = (): void => {
      this.updateLineFromHandleModification();
    };
    geometry.on('change', listener);
    this.handleChangeListener = listener;
  }

  /**
   * Handles the dragging of extension handles by inserting a new vertex.
   */
  private handleExtensionDrag(): void {
    if (!this.currentHandleType || !this.modifiedHandleFeature) {
      return;
    }

    const lineGeometry = this.lineFeature.getGeometry() as LineString;
    const coordinates = lineGeometry.getCoordinates();
    const handleGeometry = this.modifiedHandleFeature.getGeometry() as Point;
    const handleGeometryCoordinates = handleGeometry.getCoordinates();

    const newCoord: [number, number] = [...handleGeometryCoordinates] as [
      number,
      number,
    ];

    if (this.currentHandleType === 'start-extension') {
      coordinates.unshift(newCoord);
      lineGeometry.setCoordinates(coordinates);
      this.lineFeature.changed();

      this.modifiedHandleFeature.set('handleType', 'vertex');
      this.modifiedHandleFeature.set('vertexIndex', 0);

      this.handleSource.getFeatures().forEach((feature) => {
        if (feature === this.modifiedHandleFeature) {
          return;
        }
        if (feature.get('handleType') === 'vertex') {
          const existingIndex = feature.get('vertexIndex') as number;
          feature.set('vertexIndex', existingIndex + 1);
        }
      });

      this.currentHandleType = 'vertex';

      const geometry = this.modifiedHandleFeature.getGeometry() as Point;
      const listener = (): void => {
        this.updateLineFromHandleModification();
      };
      geometry.on('change', listener);
      this.handleChangeListener = listener;
    }
    else if (this.currentHandleType === 'end-extension') {
      coordinates.push(newCoord);
      lineGeometry.setCoordinates(coordinates);
      this.lineFeature.changed();

      this.modifiedHandleFeature.set('handleType', 'vertex');
      this.modifiedHandleFeature.set('vertexIndex', coordinates.length - 1);

      this.currentHandleType = 'vertex';

      const geometry = this.modifiedHandleFeature.getGeometry() as Point;
      const listener = (): void => {
        this.updateLineFromHandleModification();
      };
      geometry.on('change', listener);
      this.handleChangeListener = listener;
      this.markDirty();
    }
  }

  /**
   * Updates the LineString geometry based on the current handle modification.
   */
  private updateLineFromHandleModification(): void {
    if (!this.currentHandleType || !this.modifiedHandleFeature) {
      return;
    }

    const lineGeometry = this.lineFeature.getGeometry() as LineString;
    const coordinates = lineGeometry.getCoordinates();

    if (this.currentHandleType === 'vertex') {
      const index = this.modifiedHandleFeature.get('vertexIndex') as number;
      const newCoordinate = (
        this.modifiedHandleFeature.getGeometry() as Point
      ).getCoordinates() as [number, number];
      coordinates[index] = newCoordinate;
    }

    lineGeometry.setCoordinates(coordinates);
    this.lineFeature.changed();
    this.markDirty();
  }

  override async save(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }
    this.feature = this.lineFeature;
    await super.save();
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    this.lineFeature.setProperties(this.getDefaultProperties());
    this.lineFeature.setStyle(getFeatureStyle);
    this.markDirty();
  }

  override loadFeature(feature: Feature<Geometry>): boolean {
    if (Array.isArray(feature)) {
      return false;
    }

    const geometry = feature.getGeometry();
    if (!(geometry instanceof LineString)) {
      return false;
    }

    const coordinates = geometry.getCoordinates();

    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    const featureId = feature.get('id') as string | undefined;
    const lineGeometry = new LineString(coordinates);
    const loadedLineFeature = new Feature<LineString>(lineGeometry);
    loadedLineFeature.setProperties({
      ...this.getDefaultProperties(),
      ...featureProperties,
      id: featureId,
    });
    loadedLineFeature.setStyle(getFeatureStyle);

    this.featureId = featureId;
    this.lineFeature = loadedLineFeature;
    this.isInitialized = true;
    this.addLineHandles();
    super.loadFeature(loadedLineFeature);
    this.markDirty();
    return true;
  }
}
